/**
 * ADR-0214 Part 2 — one person (cut 1): their counts for the period, their
 * subjects (Mine's table, for them), and the work they did with no subject
 * (door calls and console actions, with what each touched). The account id is
 * the URL key only; the page shows the person's name.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { rangeLastDays } from "../components/AiUsageDashboard.js";
import { ActivityTable, DateRangeToolbar, PERSON_TABLE_HEADERS, Section, SubjectTable } from "../components/SubjectTables.js";
import { TrailLinks, useActivityTrail, useWalkedTrail } from "../components/ActivityTrail.js";
import { apiFetch } from "../lib/api.js";
import { approxTokens, countOrDash, tokens, when, whereLabel, type PersonPageResponse } from "../lib/subjects.js";
import { trailPeriod } from "../lib/activityTrail.js";

export function TenantSubjectPerson() {
  const { accountId } = useParams();
  const [params, setParams] = useSearchParams();
  // The period: the address's, else the trail's, else the last 30 days.
  const fallback = trailPeriod(useWalkedTrail()) ?? rangeLastDays(30);
  const from = params.get("from") || fallback.from;
  const to = params.get("to") || fallback.to;
  const [data, setData] = useState<PersonPageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const trail = useActivityTrail(data?.person.person ?? "Person", [{ to: `/app/activity?from=${from}&to=${to}`, label: "Activity" }]);
  const breadcrumb = <TrailLinks trail={trail.above} />;

  const load = useCallback(async () => {
    setData(null);
    setError(null);
    try {
      const q = new URLSearchParams({ from, to });
      setData(await apiFetch<PersonPageResponse>(`/api/tenant/subjects/people/${encodeURIComponent(accountId ?? "")}?${q.toString()}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [accountId, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const toolbar = <DateRangeToolbar from={from} to={to} onChange={(r) => setParams(new URLSearchParams(r), { replace: true })} />;

  if (error) {
    return (
      <PageLayout title="Person" breadcrumb={breadcrumb}>
        <ErrorState detail={error === "not_available" ? "This person is not available." : error} />
      </PageLayout>
    );
  }
  if (!data) {
    return (
      <PageLayout title="Person" breadcrumb={breadcrumb}>
        {toolbar}
        <LoadingState />
      </PageLayout>
    );
  }

  const tz = data.time_zone;
  const p = data.person;
  const headers = PERSON_TABLE_HEADERS.filter((h) => h !== "Person" && h !== "≈ Tokens");

  return (
    <PageLayout title={p.person} breadcrumb={breadcrumb} description={p.active ? undefined : "Deactivated"}>
      <div style={{ display: "grid", gap: 12 }}>
        {toolbar}

        <div className="card adminList">
          <div className="tableWrap tableWrapFrame">
            <table className="table">
              <thead>
                <tr>
                  {headers.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                  <th title="Approximate: characters exchanged with the assistant ÷ 4.">≈ Tokens (assistant)</th>
                  <th>Tokens (console AI)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{p.last_active ? when(p.last_active, tz) : "Never"}</td>
                  <td>{whereLabel(p.where)}</td>
                  <td>{countOrDash(p.subjects)}</td>
                  <td>{countOrDash(p.novel)}</td>
                  <td>{countOrDash(p.without_subject)}</td>
                  <td>{countOrDash(p.pages_viewed)}</td>
                  <td>{countOrDash(p.failed)}</td>
                  <td>{countOrDash(p.blocked)}</td>
                  <td>{countOrDash(p.concerns)}</td>
                  <td>{approxTokens(p.chars)}</td>
                  <td>{tokens(p.ai_tokens)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <Section title="Subjects">
          {data.rows.length === 0 ? (
            <div className="muted" style={{ padding: 12 }}>No subjects in this period.</div>
          ) : (
            <SubjectTable rows={data.rows} timeZone={tz} withPerson={false} range={{ from, to }} onward={trail.onward} />
          )}
        </Section>
        {data.truncated ? <div className="muted">Showing the 500 most recent. Narrow the period to see the rest.</div> : null}

        <Section title="Without a subject">
          {data.without_subject.length === 0 ? (
            <div className="muted" style={{ padding: 12 }}>No work without a subject in this period.</div>
          ) : (
            <ActivityTable rows={data.without_subject} timeZone={tz} />
          )}
        </Section>
        {data.without_subject_truncated ? <div className="muted">Showing the first 2,000 actions. Narrow the period to see the rest.</div> : null}
      </div>
    </PageLayout>
  );
}
